import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { COMPANY, PRISER_TEXT } from "../data/content";

export default function Priser() {
  return (
    <>
      <SEO
        title="Priser på hovtrimning"
        description="Se priser på certificeret hovtrimning for almindelig og tung hest, samlerakker samt årsabonnementer (fra 291,66 kr/md) i hele Jylland hos Hoofcare."
      />

      {/* Header Banner */}
      <section className="bg-forest-900 text-white py-14 lg:py-18">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-3">
          <span className="text-xs font-bold uppercase tracking-wider text-emerald-400">
            Prisoversigt & Betingelser
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-sand-50">
            Priser
          </h1>
          <p className="text-sand-200 max-w-2xl text-base leading-relaxed">
            {PRISER_TEXT.title} – Alle priser er pr. hest og med moms og kørsel.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-sand-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          
          {/* Important Rule Banner */}
          <div className="bg-amber-50 border-l-4 border-amber-600 p-5 rounded-r-xl space-y-1 shadow-2xs">
            <h2 className="text-base font-bold text-amber-900 flex items-center gap-2">
              <i className="fa-solid fa-circle-exclamation text-amber-700"></i>
              Betingelse for nye kunder
            </h2>
            <p className="text-sm font-semibold text-amber-900">
              {PRISER_TEXT.rule}
            </p>
          </div>

          <h2 className="text-2xl font-bold text-forest-900 border-b border-sand-300 pb-3">
            {PRISER_TEXT.title}
          </h2>

          {/* Pricing Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            
            {/* Card 1: 1 hest */}
            <div className="bg-white rounded-2xl p-6 border border-sand-300 shadow-2xs space-y-4 flex flex-col justify-between">
              <div className="space-y-3">
                <div className="inline-block px-3 py-1 rounded-full bg-forest-100 text-forest-900 text-xs font-bold">
                  Enkel hest
                </div>
                <h3 className="text-xl font-bold text-forest-900">Trim af 1 hest</h3>
                <div className="py-2">
                  <span className="text-3xl font-extrabold text-forest-900">475kr.</span>
                  <span className="text-xs text-charcoal-600 block mt-1 font-medium">pr. hest inkl. moms og kørsel</span>
                </div>
                <p className="text-sm text-charcoal-700 leading-relaxed">
                  Naturlig hovtrimning af 1 hest.
                </p>
              </div>

              <div className="pt-4 border-t border-sand-200">
                <Link
                  to="/kontakt"
                  className="w-full py-2.5 px-4 rounded-xl bg-forest-900 hover:bg-forest-800 text-white font-bold text-xs text-center block transition-colors cursor-pointer"
                >
                  Bestil tid
                </Link>
              </div>
            </div>

            {/* Card 2: Tung hesterace */}
            <div className="bg-white rounded-2xl p-6 border border-sand-300 shadow-2xs space-y-4 flex flex-col justify-between">
              <div className="space-y-3">
                <div className="inline-block px-3 py-1 rounded-full bg-forest-100 text-forest-900 text-xs font-bold">
                  Tung hest
                </div>
                <h3 className="text-xl font-bold text-forest-900">Trim af tung hesterace</h3>
                <div className="py-2">
                  <span className="text-3xl font-extrabold text-forest-900">525kr.</span>
                  <span className="text-xs text-charcoal-600 block mt-1 font-medium">pr. hest inkl. moms og kørsel</span>
                </div>
                <p className="text-sm text-charcoal-700 leading-relaxed">
                  Naturlig hovtrimning af svær/tung hesterace.
                </p>
              </div>

              <div className="pt-4 border-t border-sand-200">
                <Link
                  to="/kontakt"
                  className="w-full py-2.5 px-4 rounded-xl bg-forest-900 hover:bg-forest-800 text-white font-bold text-xs text-center block transition-colors cursor-pointer"
                >
                  Bestil tid
                </Link>
              </div>
            </div>

            {/* Card 3: 3+ heste i turnus */}
            <div className="bg-white rounded-2xl p-6 border-2 border-forest-700 shadow-md space-y-4 flex flex-col justify-between relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-forest-800 text-emerald-300 text-[10px] font-bold uppercase tracking-wider rounded-full">
                Grupperabat
              </div>
              <div className="space-y-3">
                <div className="inline-block px-3 py-1 rounded-full bg-forest-100 text-forest-900 text-xs font-bold">
                  3+ heste i fast turnus
                </div>
                <h3 className="text-xl font-bold text-forest-900">Fast turnus (3+ heste)</h3>
                <div className="py-2">
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-extrabold text-forest-900">425 Kr.</span>
                    <span className="text-xs text-charcoal-600">/ 475kr. for tung</span>
                  </div>
                  <span className="text-xs text-charcoal-600 block mt-1 font-medium">pr. hest inkl. moms og kørsel</span>
                </div>
                <p className="text-sm text-charcoal-700 leading-relaxed">
                  Trim af 3 eller flere heste på samme sted og de er i fast turnus på 6 uger. <strong>425 Kr.</strong> og <strong>475kr.</strong> For tunge hesterace.
                </p>
              </div>

              <div className="pt-4 border-t border-sand-200">
                <Link
                  to="/kontakt"
                  className="w-full py-2.5 px-4 rounded-xl bg-forest-900 hover:bg-forest-800 text-white font-bold text-xs text-center block transition-colors cursor-pointer"
                >
                  Bestil samlerute
                </Link>
              </div>
            </div>

            {/* Card 4: Årsabonnement almindelig hest */}
            <div className="bg-white rounded-2xl p-6 border border-sand-300 shadow-2xs space-y-4 flex flex-col justify-between">
              <div className="space-y-3">
                <div className="inline-block px-3 py-1 rounded-full bg-forest-100 text-forest-900 text-xs font-bold">
                  Fast ordning
                </div>
                <h3 className="text-xl font-bold text-forest-900">Årsabonnement – almindelig hest</h3>
                <div className="py-2">
                  <span className="text-3xl font-extrabold text-forest-900">283,33 kr.</span>
                  <span className="text-xs text-charcoal-600 block mt-1 font-medium">pr. md (6 ugers interval, 8 trim årligt)</span>
                </div>
                <p className="text-sm text-charcoal-700 leading-relaxed">
                  Årsabonnement for almindelig hest med 6 ugers interval, 8 trim årligt.
                </p>
              </div>

              <div className="pt-4 border-t border-sand-200">
                <Link
                  to="/kontakt"
                  className="w-full py-2.5 px-4 rounded-xl bg-forest-900 hover:bg-forest-800 text-white font-bold text-xs text-center block transition-colors cursor-pointer"
                >
                  Tilmeld abonnement
                </Link>
              </div>
            </div>

            {/* Card 5: Årsabonnement tung hest */}
            <div className="bg-white rounded-2xl p-6 border border-sand-300 shadow-2xs space-y-4 flex flex-col justify-between">
              <div className="space-y-3">
                <div className="inline-block px-3 py-1 rounded-full bg-forest-100 text-forest-900 text-xs font-bold">
                  Fast ordning
                </div>
                <h3 className="text-xl font-bold text-forest-900">Årsabonnement – tung hest</h3>
                <div className="py-2">
                  <span className="text-3xl font-extrabold text-forest-900">333,33 kr.</span>
                  <span className="text-xs text-charcoal-600 block mt-1 font-medium">pr. md (6 ugers interval, 8 trim årligt)</span>
                </div>
                <p className="text-sm text-charcoal-700 leading-relaxed">
                  Årsabonnement for tung hest med 6 ugers interval, 8 trim årligt.
                </p>
              </div>

              <div className="pt-4 border-t border-sand-200">
                <Link
                  to="/kontakt"
                  className="w-full py-2.5 px-4 rounded-xl bg-forest-900 hover:bg-forest-800 text-white font-bold text-xs text-center block transition-colors cursor-pointer"
                >
                  Tilmeld abonnement
                </Link>
              </div>
            </div>

          </div>

          {/* Pricing Info & Insurance Footer Box */}
          <div className="bg-white rounded-2xl p-6 sm:p-8 border border-sand-300 shadow-xs space-y-4">
            <h3 className="text-xl font-bold text-forest-900 flex items-center gap-2">
              <i className="fa-solid fa-shield-halved text-forest-700"></i>
              Prisoplysninger & Forsikring
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-charcoal-700">
              <div className="p-4 bg-sand-50 rounded-xl border border-sand-200 space-y-1">
                <h4 className="font-bold text-forest-900">Alle priser</h4>
                <p className="leading-relaxed">
                  {PRISER_TEXT.footerInfo}
                </p>
              </div>

              <div className="p-4 bg-sand-50 rounded-xl border border-sand-200 space-y-1">
                <h4 className="font-bold text-forest-900">Erhvervsforsikring</h4>
                <p className="leading-relaxed font-semibold text-forest-800">
                  {PRISER_TEXT.insurance}
                </p>
              </div>
            </div>
          </div>

          {/* CTA Banner */}
          <div className="bg-forest-900 text-white p-8 rounded-2xl text-center space-y-4 shadow-md">
            <h3 className="text-2xl font-bold text-sand-50">Klar til at booke tid til din hest?</h3>
            <p className="text-sm text-sand-200 max-w-md mx-auto">
              Kontakt mig direkte på tlf. <a href={`tel:${COMPANY.phoneRaw}`} className="underline font-bold text-emerald-300 whitespace-nowrap">{COMPANY.phone}</a> eller via formularen.
            </p>
            <div className="pt-2">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-forest-700 hover:bg-forest-600 text-white font-bold text-sm transition-colors cursor-pointer"
              >
                <i className="fa-solid fa-envelope"></i>
                <span>Gå til kontaktformular</span>
              </Link>
            </div>
          </div>

        </div>
      </section>
    </>
  );
}
